import type { PersistedToolResult } from '@hector/shared-types/casediscovery';

let legacyCounter = 0;

/**
 * Normalizes a raw tool_result entry from DB metadata into PersistedToolResult.
 *
 * LEGACY — supprimer après mai 2026.
 * Conversations pré-avril 2026 use two typed formats:
 *   { type: 'backend_tool', tool, status, data, timestamp }
 *   { type: 'jurisprudence_results', data: { response_text, ... } }
 *
 * New conversations (April 2026+) already use PersistedToolResult directly.
 * This function detects the format and converts if needed, so all downstream
 * code handles a single type.
 */
export function normalizeLegacyToolResult(raw: unknown): PersistedToolResult {
  if (!raw || typeof raw !== 'object') {
    return makeErrorResult('unknown', 'Invalid tool result entry');
  }

  const entry = raw as Record<string, unknown>;

  // New format: has `toolName` — construct explicitly (no double-cast)
  if ('toolName' in entry && typeof entry.toolName === 'string') {
    return {
      toolCallId: typeof entry.toolCallId === 'string' ? entry.toolCallId : `unknown-${++legacyCounter}`,
      toolName: entry.toolName,
      input: (entry.input && typeof entry.input === 'object' ? entry.input : {}) as Record<string, unknown>,
      result: (entry.result && typeof entry.result === 'object' ? entry.result : {}) as Record<string, unknown>,
      isError: typeof entry.isError === 'boolean' ? entry.isError : false,
      timestamp: typeof entry.timestamp === 'string' ? entry.timestamp : new Date().toISOString(),
      // Dossier de l'appel (#2602) : sans lui, la ligne d'outil cesse de nommer un
      // connexe dès que l'historique passe par ce normaliseur (rechargement).
      ...(typeof entry.caseId === 'string' && entry.caseId !== '' ? { caseId: entry.caseId } : {}),
    };
  }

  // Legacy format 1: { type: 'backend_tool', tool, status, data }
  if (entry.type === 'backend_tool' && typeof entry.tool === 'string') {
    const data = (entry.data ?? {}) as Record<string, unknown>;
    return {
      toolCallId: `legacy-${++legacyCounter}`,
      toolName: entry.tool,
      input: data,
      result: data,
      isError: entry.status === 'error',
      timestamp: (entry.timestamp as string) ?? new Date().toISOString(),
    };
  }

  // Legacy format 2: { type: 'jurisprudence_results', data: {...} }
  if (entry.type === 'jurisprudence_results' && entry.data) {
    return {
      toolCallId: `legacy-${++legacyCounter}`,
      toolName: 'search_jurisprudence',
      input: {},
      result: entry.data as Record<string, unknown>,
      isError: false,
      timestamp: (entry.timestamp as string) ?? new Date().toISOString(),
    };
  }

  return makeErrorResult('unknown', 'Unrecognized tool result format');
}

function makeErrorResult(toolName: string, message: string): PersistedToolResult {
  return {
    toolCallId: `legacy-${++legacyCounter}`,
    toolName,
    input: {},
    result: { error: message },
    isError: true,
    timestamp: new Date().toISOString(),
  };
}
